import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';

import CycleSelector from 'components/cycle-selector';
import { useAuth } from 'context/AuthContext';
import LogoImg from 'assets/img/logo-jj.png';
import { SAIDA_DELIBERADA, VERSION } from 'utils/constants';

import { ChevronDownIcon } from './icons';
import { NavGroup, NavLink, isNavGroup } from './menu-items';
import { useMenuEntries } from './useMenuEntries';
import './styles.scss';

const ITEM_BASE = 'jj-nav-item flex items-center w-full box-border py-[13px] px-[17px] font-display text-[15px] no-underline';

const ITEM_IDLE = `${ITEM_BASE} gap-2 border-b border-white/25 font-semibold`;

const ITEM_ACTIVE = `${ITEM_BASE} jj-nav-item--ativo gap-2.5 rounded-lg bg-brand-white font-bold`;

const SUB_ITEM = 'jj-nav-sub py-[11px] pr-[17px] pl-[26px] rounded-md font-display font-semibold text-[14px] no-underline';

const isActivePath = (pathname: string, to: string) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`));

type SidebarProps = {
  onNavigate?: () => void;
};

export default function Sidebar({ onNavigate }: SidebarProps) {
  const { signOut, isMSTeams } = useAuth();
  const { instance } = useMsal();
  const { pathname } = useLocation();
  const entries = useMenuEntries();

  function handleSignOut() {
    signOut();
    // Quem redireciona para o login é o PrivateLayout, ao ver a sessão caída,
    // e ele não carrega hash. O sinal em sessionStorage atravessa esse salto
    // para a entrada saber que foi saída deliberada, e não sessão expirada.
    sessionStorage.setItem(SAIDA_DELIBERADA, '1');
    instance.logoutRedirect({ onRedirectNavigate: () => false });
  }

  const renderLink = ({ label, to, icon }: NavLink, className = '') => {
    const active = isActivePath(pathname, to as string);

    return (
      <Link
        key={label}
        to={to as string}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={className || (active ? ITEM_ACTIVE : ITEM_IDLE)}
      >
        {icon}
        {label}
      </Link>
    );
  };

  const renderGroup = ({ label, children }: NavGroup) => {
    const algumFilhoAtivo = children.some(child => !!child.to && isActivePath(pathname, child.to));

    return (
      <details key={label} className="jj-nav-group" open={algumFilhoAtivo}>
        <summary className={`${algumFilhoAtivo ? ITEM_ACTIVE : ITEM_IDLE} justify-between`}>
          <span>{label}</span>
          <ChevronDownIcon />
        </summary>

        <div className="flex flex-col pt-1 pb-1.5">
          {children.map((child, index) =>
            renderLink(child, index < children.length - 1 ? `${SUB_ITEM} border-b border-white/[0.18]` : SUB_ITEM),
          )}
        </div>
      </details>
    );
  };

  return (
    <aside aria-label="Barra lateral" className="w-full max-w-[351px] shrink-0 box-border flex flex-col items-center gap-[22px] px-[26px] pt-[34px] pb-[38px] rounded-2xl bg-brand-blue shadow-[0_1px_1px_rgba(21,21,24,.18),0_0_1px_rgba(25,25,28,.25)]">
      <Link to="/" onClick={onNavigate} aria-label="Ir para a página inicial">
        <img src={LogoImg} alt="Joga Junto BB" className="block w-[222px]" />
      </Link>

      <p className="mb-1 text-center font-display font-bold text-[23px] leading-[1.15] text-brand-yellow">
        Uma vida de
        <br />
        oportunidades!
      </p>

      {/* CIC-01: o ciclo do app em todas as telas (e no menu aberto do celular), não só na página inicial. */}
      <div className="relative w-full">
        <CycleSelector rotulo="Selecione o ciclo no menu" />
      </div>

      <nav className="flex w-full flex-col" aria-label="Menu principal">
        {entries.map(entry => (isNavGroup(entry) ? renderGroup(entry) : renderLink(entry)))}

        {!isMSTeams && (
          <button type="button" onClick={handleSignOut} className={`${ITEM_IDLE} border-b-0 text-left`}>
            Sair
          </button>
        )}
      </nav>

      {/* Branco pleno: com 70% de opacidade o texto de 11px dava 3,21:1 sobre o azul (AA pede 4,5:1). */}
      <span data-app-version className="text-[11px] text-brand-white" aria-hidden="true">
        {VERSION}
      </span>
    </aside>
  );
}
